import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Users, ArrowLeft, Copy, Printer, Clock } from 'lucide-react';
import { api, API } from '../lib/api';
import { toast } from 'sonner';

const ORDER = ['not_started', 'researching', 'preparing_docs', 'submitted', 'under_review', 'approved', 'denied'];
const LABEL = { not_started: 'Not started', researching: 'Researching', preparing_docs: 'Preparing docs', submitted: 'Submitted', under_review: 'Under review', approved: 'Approved', denied: 'Denied', unclaimed: 'Unclaimed' };
const COLOR = { not_started: 'bg-[#F5F5F4] text-[#78716C]', researching: 'bg-[#E0F2FE] text-[#0284C7]', preparing_docs: 'bg-[#FEF3C7] text-[#D97706]', submitted: 'bg-[#EDE9FE] text-[#7C3AED]', under_review: 'bg-[#DBEAFE] text-[#2563EB]', approved: 'bg-[#DCFCE7] text-[#16A34A]', denied: 'bg-[#FEE2E2] text-[#DC2626]', unclaimed: 'bg-[#FFF4E5] text-[#663C00]' };

export default function PartnerDashboard({ team = false }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(team ? '/orgs/dashboard' : '/partner/dashboard').then((r) => setD(r.data)).catch(() => toast.error('Could not load dashboard')); }, []);
  const copy = (id) => { navigator.clipboard?.writeText(`${window.location.origin}/handoff/${id}`); toast.success('Link copied'); };

  if (!d) return <div className="max-w-6xl mx-auto px-4 py-16 text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="partner-dashboard">
      <Link to="/partner" className="text-sm text-primary inline-flex items-center gap-1 mb-4" data-testid="dash-back"><ArrowLeft className="w-4 h-4" /> Back to Partner Mode</Link>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1E5888]/10 text-[#1E5888] text-xs font-medium mb-3 ml-4"><Users className="w-3.5 h-3.5" /> {team ? 'Team dashboard' : 'Caseworker dashboard'}</div>
      <h1 className="text-3xl sm:text-4xl font-bold" style={{fontFamily: 'Lexend'}}>{team ? `${d.org_name}: every client across the team` : 'All your clients at a glance'}</h1>
      <p className="text-muted-foreground mt-2">{d.n_clients} checklist{d.n_clients === 1 ? '' : 's'} · {d.n_claimed} claimed by clients. Statuses update live as clients move applications forward.</p>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mt-6" data-testid="dash-totals">
        {['unclaimed', ...ORDER].map((k) => (
          <div key={k} className={`rounded-xl p-3 ${COLOR[k]}`} data-testid={`dash-total-${k}`}>
            <div className="text-2xl font-bold" style={{fontFamily: 'Lexend'}}>{d.totals[k]}</div>
            <div className="text-xs font-medium">{LABEL[k]}</div>
          </div>
        ))}
      </div>

      {team && d.per_worker && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="team-per-worker">
          {Object.entries(d.per_worker).map(([w, v]) => (
            <Card key={w} className="p-4 bg-card" data-testid={`team-worker-${w.replace(/\s+/g, '-')}`}>
              <div className="font-semibold" style={{fontFamily: 'Lexend'}}>{w}</div>
              <div className="text-xs text-muted-foreground mt-1">{v.clients} clients · {v.claimed} claimed · {v.submitted_plus} submitted or further</div>
            </Card>
          ))}
        </div>
      )}

      {d.clients.length === 0 ? <Card className="p-8 mt-6 bg-card text-center text-muted-foreground">No checklists yet. Create one in Partner Mode.</Card> : (
        <div className="mt-6 space-y-3">
          {d.clients.map((c) => (
            <Card key={c.share_id} className="p-4 sm:p-5 bg-card" data-testid={`dash-client-${c.share_id}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-lg" style={{fontFamily: 'Lexend'}}>{c.client_name}</span>
                    {team && c.worker_name && <Badge variant="outline" data-testid={`dash-worker-${c.share_id}`}>{c.worker_name}</Badge>}
                    {c.sharing_paused ? <Badge className="border-0 bg-[#FFF4E5] text-[#663C00]" data-testid={`dash-paused-${c.share_id}`}>Sharing paused by client</Badge> : c.claimed ? <Badge className={`border-0 ${COLOR[c.furthest || 'not_started']}`} data-testid={`dash-furthest-${c.share_id}`}>Furthest: {LABEL[c.furthest || 'not_started']}</Badge> : <Badge className={`border-0 ${COLOR.unclaimed}`}><Clock className="w-3 h-3 mr-1" /> Not opened yet{c.nudge_sent_at ? ' · nudged' : ''}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Created {new Date(c.created_at).toLocaleDateString()}{c.last_activity ? ` · last activity ${new Date(c.last_activity).toLocaleDateString()}` : ''}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => copy(c.share_id)} aria-label="Copy link"><Copy className="w-4 h-4" /></Button>
                  <Button asChild size="sm" variant="ghost" aria-label="Print PDF"><a href={`${API}/share/${c.share_id}/pdf`} target="_blank" rel="noopener noreferrer"><Printer className="w-4 h-4" /></a></Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {c.programs.map((p) => (
                  <span key={p.program_id} className={`text-xs px-2 py-1 rounded-md ${COLOR[p.status || 'unclaimed']}`} data-testid={`dash-prog-${c.share_id}-${p.program_id}`}>{p.name.replace(/\s*\(.*?\)\s*$/, '')} · {LABEL[p.status || 'unclaimed']}</span>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
